/**
 * FAQ knowledge base used both for on-page rendering AND schema.org FAQPage JSON-LD.
 *
 * Two sets:
 *   • getHomeFaqs()               — general eSIM questions (site-wide)
 *   • getDestinationFaqs(name)    — country/region-specific questions
 *
 * Each returns [{ question, answer }, …]. Answers should be plain text
 * (no HTML) because Google's FAQ rich-result parser strips markup and
 * penalises long answers. Keep each answer under ~250 chars for best
 * SERP display and question rewards.
 */

export const getHomeFaqs = () => [
  {
    question: 'What is an eSIM and how does it work?',
    answer:
      'An eSIM is a digital SIM card built into your phone. Instead of swapping a physical card, you scan a QR code to activate a data plan. It works alongside your regular SIM, so you keep your normal number while adding a travel data plan.',
  },
  {
    question: 'Which phones and devices support eSIM?',
    answer:
      'iPhone XS and newer, Google Pixel 3 and newer, Samsung Galaxy S20 and newer, and most flagship phones released after 2018 support eSIM. You can check by dialling *#06# — if you see an EID number, your phone is eSIM-compatible.',
  },
  {
    question: 'How quickly does a Connectify eSIM activate?',
    answer:
      'Activation is instant. Once you complete checkout you receive a QR code by email. Scan it in Wi-Fi before you travel (or on arrival) and your plan is live within seconds.',
  },
  {
    question: 'Do I keep my regular phone number while using a travel eSIM?',
    answer:
      'Yes. Your Connectify eSIM runs alongside your physical SIM as a second line. Calls and texts on your primary number still work, and data automatically uses the travel eSIM.',
  },
  {
    question: 'Can I top up my eSIM if I run out of data?',
    answer:
      'Yes. Log into your Connectify dashboard, choose your active eSIM, and select a top-up plan. Your expiry date is automatically extended by the top-up duration.',
  },
  {
    question: 'What happens if my eSIM plan expires while I am abroad?',
    answer:
      'You can top up the same eSIM instantly from your Connectify dashboard, no need to install a new one. Alternatively, buy a fresh plan and install the new QR code.',
  },
  {
    question: 'Are Connectify eSIMs 5G-ready?',
    answer:
      '5G is available on our plans wherever the local carrier supports it — including most major cities in the USA, UK, EU, Japan, South Korea, Australia, and the UAE.',
  },
  {
    question: 'Is there a free trial or refund policy?',
    answer:
      'If your eSIM fails to activate we will refund you in full. Because eSIMs are digital and instantly delivered, we cannot refund plans that have already been activated and used.',
  },
];

export const getDestinationFaqs = (destinationName, minPriceAud, isRegion) => {
  const name = destinationName || 'this destination';
  const priceStr =
    typeof minPriceAud === 'number' && minPriceAud > 0
      ? `A$${minPriceAud.toFixed(2)}`
      : 'a low price';

  return [
    {
      question: `Does an eSIM work in ${name}?`,
      answer: `Yes. Connectify partners with major local carriers across ${name}, giving you reliable 4G/5G coverage from the moment you land. Just install the QR code before you travel.`,
    },
    {
      question: `How much does a ${name} eSIM cost?`,
      answer: `Prepaid eSIM plans for ${name} start from ${priceStr} on Connectify. Pricing depends on the amount of data and days of validity you choose.`,
    },
    {
      question: `How much data do I need for a trip to ${name}?`,
      answer: `For light use (maps, messaging, occasional social media) 1–3 GB per week is plenty. Heavy users streaming video, uploading photos, or hotspotting should choose 5 GB+ or an unlimited plan.`,
    },
    {
      question: `Can I use my Connectify ${name} eSIM to make calls?`,
      answer: `Connectify travel eSIMs are data-only plans. You can still make calls over Wi-Fi calling, WhatsApp, FaceTime, or Google Voice using the eSIM data.`,
    },
    {
      question: isRegion
        ? `Does the ${name} regional plan work across every country in the region?`
        : `Do I need to change my phone settings when I arrive in ${name}?`,
      answer: isRegion
        ? `Yes — one plan gives you seamless coverage across all countries included in the ${name} regional plan. Data works as soon as you cross a border.`
        : `Only two settings: enable data roaming for the Connectify line, and set it as your default data line. That's it — no APN changes required for most phones.`,
    },
    {
      question: `When should I install my ${name} eSIM?`,
      answer: `You can install it any time — even weeks before your trip. The plan only starts counting down validity once you enable it on the device. Install on Wi-Fi at home for the easiest setup.`,
    },
  ];
};

/**
 * Build the schema.org FAQPage JSON-LD block from a list of QAs.
 * Pass the returned object into <SEO structuredData={[...]}/>.
 */
export const buildFaqLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (faqs || []).map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
});
